import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sparkles, ChevronDown, ChevronUp, Brain, Rocket, MessageSquare, Zap } from 'lucide-react';
import './LukosAIPremium.css';

const FeatureAccordion = ({ items }: { items: any[] }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="laip-accordion">
      {items.map((item, index) => (
        <div 
          key={index} 
          className={`laip-acc-item ${activeIndex === index ? 'active' : ''}`}
          onClick={() => setActiveIndex(index)}
        >
          <div className="laip-acc-header">
            <h3>{item.title}</h3>
            {activeIndex === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          <AnimatePresence>
            {activeIndex === index && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="laip-acc-content"
              >
                {item.description}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export const LukosAIHero = () => {
  return (
    <section className="laip-section" id="lukos-ai-premium">
      <div className="laip-container">
        <div className="laip-hero">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="laip-hero-content"
          >
            <div className="mb-8">
              <a href="/#solucoes-interativas" className="inline-flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-slate-800 transition-colors">
                <ArrowLeft size={16} /> Voltar para visão geral
              </a>
            </div>
            <div className="laip-brand-tag">
              <span>lukos</span> 
              <span className="laip-brand-ai">AI <Sparkles size={18} className="ml-1" /></span>
            </div>
            <h1 className="laip-title">
              Inteligência que Antecipa. <br />
              <span>Gestão que Escala.</span>
            </h1>
            <p className="laip-description">
              Consulte dados e peça tarefas do ERP em linguagem natural. A LUK respeita as permissões de cada usuário e só executa depois da sua confirmação.
            </p>
            <div className="laip-hero-actions">
              <a 
                href="https://wa.me/5511978747233?text=Ol%C3%A1!%20Gostaria%20de%20contratar%20o%20servi%C3%A7o%20Luk%20IA." 
                target="_blank"
                rel="noopener noreferrer"
                className="laip-btn-primary shadow-xl"
              >
                Contratar serviço <ArrowRight size={18} />
              </a>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="laip-hero-visual"
          >
            <div className="laip-blob-container">
              <img 
                src="/assets/lukos_ai_premium_mockup_3d.png" 
                alt="Lukos AI Intelligence" 
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const LukosAIFeatures = () => {
  const features1 = [
    { 
      title: 'Invista tempo no que importa', 
      description: 'Elimine até 40% das atividades manuais e repetitivas da sua equipe e ganhe tempo para estratégia, segurança e inovação.' 
    },
    { 
      title: 'Extraia e leia dados complexos', 
      description: 'Nossa IA lê documentos fiscais, faturas e relatórios automaticamente, extraindo dados críticos sem erro humano com 99% de precisão.' 
    },
    { 
      title: 'Análise por Linguagem Natural', 
      description: 'Pergunte ou peça uma tarefa no ERP com suas palavras. A LUK consulta o que você pode ver e confirma antes de alterar qualquer dado.' 
    }
  ];

  return (
    <section className="laip-section">
      <div className="laip-container">
        <div className="laip-feature-grid">
          <div className="laip-mockup-display" style={{ background: 'linear-gradient(135deg, #7f00ff, #e100ff)' }}>
            <motion.div 
              initial={{ y: 60, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="laip-mockup-card"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center p-2">
                    <img src="/icons/Logo4kIcon.png" alt="Lukos Logo" className="w-full h-full object-contain" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800">Lukos AI</h4>
                    <span className="text-[10px] text-green-500 font-bold uppercase tracking-widest">Ativo agora</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {[1,2,3].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-slate-200" />)}
                </div>
              </div>
              
              <div className="laip-ai-prompt border border-purple-100 bg-slate-50/50 backdrop-blur-sm">
                <div className="laip-ai-badge shadow-lg shadow-purple-500/20">
                  <Sparkles size={12} /> Peça à IA
                </div>
                <p className="text-sm font-extrabold text-slate-800 mb-3">Escreva o que o Lukos AI tem que fazer</p>
                <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-500 mb-5 shadow-sm italic">
                  "Analise o faturamento dos últimos 3 meses e sugira uma meta de redução de custos operacionais com base nos bicos de maior vazão..."
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {[1,2].map(i => (
                      <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 overflow-hidden">
                        <img src={`https://i.pravatar.cc/150?u=${i}`} alt="user" />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-[10px] font-bold border border-blue-100">Relatório.pdf</div>
                    <div className="px-3 py-1.5 bg-green-50 text-green-600 rounded-lg text-[10px] font-bold border border-green-100">Vendas.xlsx</div>
                  </div>
                </div>
              </div>

              {/* Decorative UI elements */}
              <div className="absolute -right-4 -bottom-4 w-32 h-32 bg-white rounded-2xl shadow-2xl p-4 border border-slate-100 hidden md:block">
                <div className="w-full h-2 bg-slate-100 rounded-full mb-2" />
                <div className="w-2/3 h-2 bg-slate-100 rounded-full mb-4" />
                <div className="flex items-end gap-1 h-12">
                  {[40, 70, 50, 90].map((h, i) => (
                    <div key={i} className="flex-1 bg-purple-500 rounded-t-sm" style={{ height: `${h}%` }} />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          <div className="laip-feature-content">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              Automatize o que precisar — é só pedir
            </motion.h2>
            <p className="laip-feature-description">
              Consulte relatórios, estoque e resultados, ou peça tarefas no ERP em linguagem natural, com permissão e confirmação.
            </p>
            <FeatureAccordion items={features1} />
          </div>
        </div>
      </div>
    </section>
  );
};

export const LukosAIStrategy = () => {
  const features2 = [
    { title: 'Otimize sua operação em minutos', description: 'Descreva seu objetivo ideal usando linguagem natural e veja a inteligência Lukos transformar sua gestão.' },
    { title: 'Delegue a análise de dados', description: 'Deixe a IA processar milhares de transações e identificar padrões que passariam despercebidos.' },
    { title: 'Tome decisões com segurança', description: 'Receba recomendações baseadas em dados reais, mitigando riscos e aproveitando oportunidades de mercado.' }
  ];

  return (
    <section className="laip-section">
      <div className="laip-container">
        <div className="laip-feature-grid">
          <div className="laip-feature-content">
            <h2>Crie, analise e desenvolva sua estratégia</h2>
            <p className="laip-feature-description">
              Evolua sua operação, acompanhe dados e tome decisões estratégicas com a inteligência Lukos.
            </p>
            <FeatureAccordion items={features2} />
            <Link to="/contato" className="laip-btn-secondary mt-8 flex items-center gap-2">
               Falar com especialista
            </Link>
          </div>

          <div className="laip-mockup-display" style={{ background: 'linear-gradient(135deg, #0052cc, #00c6ff)' }}>
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              className="laip-mockup-card"
            >
              <div className="border-b border-slate-100 pb-4 mb-4 flex justify-between">
                <span className="font-bold text-slate-400 text-sm">lukos AI</span>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-slate-200" />
                  <div className="w-2 h-2 rounded-full bg-slate-200" />
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center p-1.5">
                    <img src="/icons/Logo4kIcon.png" alt="Lukos Logo" className="w-full h-full object-contain brightness-0 invert" />
                  </div>
                  <div className="bg-slate-100 rounded-2xl rounded-tl-none p-3 text-sm text-slate-700 max-w-[80%]">
                    Olá! Como posso ajudar na estratégia do seu posto hoje?
                  </div>
                </div>
                <div className="pt-20">
                  <div className="border border-purple-200 rounded-full p-4 flex justify-between items-center bg-white">
                    <span className="text-xs text-slate-400">Qual é a porcentagem de lucro líquido por bico...</span>
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">
                      <ArrowRight size={14} />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
